// DT-1c — **THE READING HALF OF THE DELIVERY BEACON, AND THE CLAUSE DT-1 SHIPPED WITHOUT.**
//
// Her order's own words: *"A send with no received-beacon inside an hour is UNDELIVERED, loudly, on
// the health strip, with the member and a probable cause named (stale endpoint, revoked permission,
// worker failure)."* DT-1 built the chain and DT-1b held its crossing. **Nothing read it.** Measured
// before a line of this was written (R25): `undelivered`, `deliveryAudit`, `chainsForMember` and
// `chainFor` in `server/db/push-beacons.js` had **ZERO call sites outside their own module** — R38's
// trap, on the instrument the all-members morning open is gated on. A capability wired to nothing has
// no error state, and this one's whole job is to be the error state.
//
// This module imports NOTHING, so the SERVER composes the payload with it and the DASHBOARD renders
// from it, and `scripts/dt1c-delivery-strip.mjs` RUNS every line of it with no database and no
// browser. One law, two readers — `src/commandCenter.js`'s own shape, one system over.
//
// **THE THREE THINGS THIS FILE REFUSES TO DO.**
//   1. **IT NEVER GUESSES A CAUSE.** Every verdict below is derived from a column something else
//      already wrote, and where the evidence cannot say, the answer is `unknown` and it SAYS so
//      (R18/R19). A confident wrong cause sends a session to fix the wrong layer, which is more
//      expensive than no cause at all.
//   2. **IT NEVER CALLS AN EMPTY WINDOW CLEAN.** A strip reading "0 undelivered" over a window in
//      which nothing was sent is R43's M8 arriving on the one instrument built to prove pushes land:
//      a filter over an empty list passes by finding nothing. `quiet` is its own state.
//   3. **IT NEVER COLLAPSES THE TWO STAMPS.** Migration 075 keeps `received_at` and `displayed_at`
//      apart because a push that reaches the worker and fails to DRAW is a different defect from one
//      that never arrives, and the last three incidents were indistinguishable precisely because
//      something upstream had collapsed them into a boolean. Both are counted, separately, here.

// ---------------------------------------------------------------------------
// THE CAUSES. **A ROSTER, NOT A STRING AT A CALL SITE** (R17/R28/R29), because the strip, the fixture
// and anything later that wants to act on one must all be naming the same four things.
//
// `act` is what SHE can do about it, and it is part of the roster rather than prose on the screen:
// three of these four heal themselves on her member's next app open and one does not, and a strip
// that does not say which is a strip that produces a to-do list nobody can work.
// ---------------------------------------------------------------------------
export const DELIVERY_CAUSES = [
  {
    key: "endpoint-gone",
    label: "No device registered",
    // `recordSendResult` DELETES on a 404/410 — every push service's way of saying the device threw
    // this subscription away — so the absence of a row is not missing evidence, it is the push
    // service's own verdict, written by us, after this send. A beacon row cannot exist without a
    // device having existed at send time (`pushToMember` returns `no-devices` before it stamps one),
    // so this state is always a device that went away AFTER we sent.
    why: "Every device row for her is gone. Either the push service refused the last one as dead, or she turned notifications off.",
    act: "Heals itself: her next open of the installed app re-mints an endpoint.",
  },
  {
    key: "grant-gone",
    label: "Permission gone",
    // The device row survives and the account grant does not. P3-2's heal clears the answer with
    // `clear-stale-grant-and-ask` precisely when the browser permission has died under a stored yes,
    // so this is the heal having already seen it.
    why: "Her device is still registered and her permission is not — the heal clears the grant when the browser permission dies under it.",
    act: "Heals itself: she is re-asked on her next open, and the ask is the only lawful way back.",
  },
  {
    key: "never-accepted",
    label: "Never left the building",
    // A device in soft failure that has not succeeded since this send. `recordSendResult` stamps
    // `failed_at` on a non-`gone` refusal, so this is the push service saying no to us rather than a
    // phone ignoring us — and the difference decides who has to fix it.
    why: "The push service refused the send. It never reached a device at all, so this is not a phone that ignored us.",
    act: "Ours. Read the push receipt for the status and body the service returned.",
  },
  {
    key: "worker-silent",
    label: "Accepted, never reported",
    // The strongest evidence in the table and the reason `last_seen_at` is read here: it is written
    // ONLY on `result.ok`, so a stamp at or after this send is the push service's 201 in a column.
    // Past that point everything is on the device, which is the whole finding DT-1 exists to make.
    why: "The push service accepted it and the device never answered. Past that point it is the app on her phone, not the endpoint.",
    act: "Ours, and it is DT-2's: the send is real and the worker is what did not report.",
  },
  {
    key: "worker-not-adopted",
    label: "Sent before her app woke up",
    // **L4 — THE FIFTH CAUSE, AND HER OWN HUNT IS WHY IT EXISTS.** The order read: *"Eria's worker
    // HAS adopted and DOES answer — `post:393` and `message:27` both came back — while both
    // daily-line sends produced no beacon. So it is not worker adoption; it is the daily line
    // specifically."* **Read in TIME order rather than by kind, her four sends are: daily-line
    // silent, then message ARRIVED, then post ARRIVED, then daily-line ARRIVED.** Everything before
    // her first-ever beacon is silent and everything after it is fine — including the daily line.
    // The discriminator was never the KIND, it was whether the new service worker had activated yet
    // (PN1: it WAITS, and takes over when she next opens the app).
    //
    // **AND TELLING IT APART FROM `worker-silent` IS NOT A LABEL, IT IS WHAT THE HEAL DOES.**
    // DT-2a re-mints an endpoint on every launch when the house reports `worker-silent`; for a member
    // whose worker has never once reported, that is a repair loop with nothing at the end of it. This
    // cause is deliberately NOT on `HOUSE_SILENT_CAUSE`.
    why: "This went out before her app had ever reported anything at all — the new worker had not taken over yet, which it does on her next open.",
    act: "Nothing to repair: it resolves itself the first time she opens the app, and her sends after that one are the evidence.",
  },
  {
    key: "unknown",
    label: "Cause not recorded",
    // R18/R19. Not a fifth failure mode — the honest name for evidence that cannot say, kept on the
    // roster so it is counted and ranked beside the four rather than hidden inside one of them.
    why: "Not enough is recorded about this send to name a cause.",
    act: "Nothing to act on yet. A cause named wrongly is more expensive than one not named.",
  },
];

export const DELIVERY_CAUSE_KEYS = DELIVERY_CAUSES.map((c) => c.key);
export const deliveryCause = (key) =>
  DELIVERY_CAUSES.find((c) => c.key === String(key || "")) || DELIVERY_CAUSES[DELIVERY_CAUSES.length - 1];

// **A TIMESTAMP ARRIVES AS THREE THINGS AND `new Date(null)` IS THE EPOCH, NOT NaN.** ITEM 10 paid
// for that exact line one law over: Postgres hands `timestamptz` back as a Date, a JSON round trip
// makes it a string, and a fixture writes a number — so null is guarded EXPLICITLY rather than being
// allowed to fall through as 1970, which would make every absent stamp look like the oldest evidence
// in the table.
function ms(v) {
  if (v === null || v === undefined || v === "") return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

// ---------------------------------------------------------------------------
// THE VERDICT. One send, the evidence around it, one cause.
//
// **THE ORDER OF THE CLAUSES IS THE LAW AND IT IS HELD AS AN ORDER**, because two of them can be true
// at once and they are not equally informative: a member with no device row AND a cleared grant is
// `endpoint-gone` (there is nothing to send to), and reporting `grant-gone` would send somebody
// looking at a permission that has no endpoint under it either way.
// ---------------------------------------------------------------------------
export function undeliveredCause({
  devices = null, answer = null, failedAt = null, lastSeenAt = null, sentAt = null,
  // L4 — had this member's app reported anything BEFORE this send? THREE values and not two
  // it has, `false` it never has, and **`null` nobody asked** — which keeps the answer exactly what
  // it was before this evidence existed, because an unmeasured lane may not invent a new verdict.
  reportedBefore = null,
} = {}) {
  // **"SHE HAS NO DEVICES" AND "NOBODY COUNTED" ARE DIFFERENT FACTS** (R18), and the second must not
  // wear the first's verdict — a caller that failed to gather evidence would otherwise report every
  // silent send as a dead endpoint and read exactly like a real finding.
  //
  // **THE TYPE IS PART OF THE TEST, WHICH IS R137's OWN LESSON AND THIS ROUND'S FIXTURE CAUGHT IT
  // BREAKING HERE:** `Number(null)` is 0 and `Number(false)` is 0, so an absent count written as
  // `Number(devices)` is finite, lands on the `<= 0` arm, and names `endpoint-gone` about a send
  // nobody gathered any evidence for at all. Only a real number, or a string of digits Postgres could
  // have handed back, is a device count.
  const n = typeof devices === "number" ? devices
    : (typeof devices === "string" && devices.trim() !== "" ? Number(devices) : NaN);
  if (!Number.isFinite(n)) return "unknown";
  if (n <= 0) return "endpoint-gone";
  // Her account grant. `granted` is the only value that means she is opted in; `later`, `denied` and
  // a CLEARED field (the heal's own act) all mean the permission under that device row is not there.
  if (String(answer || "") !== "granted") return "grant-gone";

  const sent = ms(sentAt);
  const seen = ms(lastSeenAt);
  const failed = ms(failedAt);
  // **THE ACCEPTANCE IS READ OFF A COLUMN ONLY A SUCCESS WRITES.** `recordSendResult` sets
  // `last_seen_at = now()` and clears `failed_at` on `result.ok` and on nothing else, so a stamp at
  // or after this send is the push service's own 201. No new column, no migration, no second record
  // of a fact the table already holds (R28/R29).
  if (sent !== null && seen !== null && seen >= sent) {
    // **L4: THE SERVICE ACCEPTED IT EITHER WAY — WHAT DIFFERS IS WHETHER THIS APP HAS EVER SPOKEN.**
    // A worker that reported yesterday and is quiet today is a failure; a worker that has never
    // reported at all has simply not taken over yet (PN1: the new one WAITS). Her own hunt drew the
    // wrong conclusion from exactly this state, and DT-2a's heal re-mints on the first of the two.
    return reportedBefore === false ? "worker-not-adopted" : "worker-silent";
  }
  if (failed !== null && (sent === null || failed >= sent)) return "never-accepted";
  return "unknown";
}

// ---------------------------------------------------------------------------
// THE STRIP. **HER ORDER ASKS FOR THE MEMBER AND THE CAUSE, SO BOTH ARE BUILT AND THEY ARE KEPT
// APART** — DL-3.3's ruling one instrument over: a total is not a pattern, and a pattern is not a
// person she can go and ring.
//
//   `causes`  — ranked, so the thing happening MOST is the thing she reads first
//   `members` — one line per member however many sends went silent for her, so one broken phone is
//               one row rather than a wall that buries the other nine members behind it
//
// **THE GRACE IS NOT REDECLARED HERE.** `UNDELIVERED_AFTER_MINUTES` lives in the store beside the
// query that applies it; a second copy in the file that PRINTS it is how the screen comes to promise
// an hour while the sweep uses ninety minutes.
// ---------------------------------------------------------------------------
export const DELIVERY_QUIET_NOTE = "Nothing has been sent in this window, so there is nothing here to be clean.";
export const DELIVERY_BROKEN_NOTE = "Sent, and the device never answered. Until a beacon comes back these did not arrive.";

export function deliveryStrip(report) {
  const r = report && typeof report === "object" ? report : null;
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const rows = Array.isArray(r && r.undelivered) ? r.undelivered : [];
  const sent = n(r && r.sent);
  const received = n(r && r.received);
  const displayed = n(r && r.displayed);
  // **THE SECOND STAMP'S OWN GAP, COUNTED SEPARATELY** (migration 075's stated reason for existing) —
  // and counted off the LIST rather than derived as `received - displayed`. Both would be right today
  // and only one of them is a fact: subtraction can go negative on a partial read and would print a
  // nonsense number at her, and having two ways to reach one figure is how the two stop agreeing
  // (R28/R29). The fixture holds the arithmetic against the list rather than either replacing it.
  const gaps = Array.isArray(r && r.drewNothing) ? r.drewNothing : [];
  const drewNothing = gaps.length;

  const byCause = new Map();
  const byMember = new Map();
  for (const row of rows) {
    const cause = DELIVERY_CAUSE_KEYS.includes(String(row && row.cause))
      ? String(row.cause)
      // An unrostered cause is REFUSED into `unknown` rather than printed raw (R44): the strip is not
      // the place a typo in a store becomes a fifth failure mode she starts looking for.
      : "unknown";
    byCause.set(cause, (byCause.get(cause) || 0) + 1);
    // **THE MEMBER IS NAMED BY WHATEVER THE ROW REALLY CARRIES.** A send whose member row is gone has
    // no address, and inventing one is worse than saying so — D6's own door law: a door onto nothing
    // teaches her not to trust the ones that work.
    const who = String((row && (row.email || row.who)) || "").trim() || (row && row.userId ? `member ${row.userId}` : "unknown member");
    const cur = byMember.get(who) || { who, n: 0, causes: [], minutes: 0, kinds: [] };
    cur.n += 1;
    if (!cur.causes.includes(cause)) cur.causes.push(cause);
    const kind = String((row && row.kind) || "").trim();
    if (kind && !cur.kinds.includes(kind)) cur.kinds.push(kind);
    cur.minutes = Math.max(cur.minutes, Math.round(n(row && row.minutesSilent)));
    byMember.set(who, cur);
  }

  // Ranked by count, then by NAME, so two equal counts do not swap places between refreshes and read
  // as movement (DL-3.3's own tiebreak, and the same reason).
  const causes = [...byCause.entries()]
    .map(([cause, count]) => ({ cause, n: count, label: deliveryCause(cause).label }))
    .sort((a, b) => b.n - a.n || a.cause.localeCompare(b.cause));
  const members = [...byMember.values()].sort((a, b) => b.n - a.n || b.minutes - a.minutes || a.who.localeCompare(b.who));

  // **THREE STATES, NOT TWO** (R18, and it is the load-bearing line in this file). `quiet` is a window
  // with no evidence in it and `clean` is a window full of arrivals; a strip that draws them the same
  // way is green about a push system that could be entirely dead.
  const state = sent === 0 ? "quiet" : (rows.length || drewNothing) ? "broken" : "clean";
  return {
    window: n(r && r.window),
    graceMinutes: n(r && r.graceMinutes),
    sent, received, displayed, drewNothing,
    undelivered: rows.length,
    causes, members, state,
  };
}

// **ONLY THE TWO STATES THAT MEAN SOMETHING SAY ANYTHING.** A strip that congratulates her on a clean
// morning is a strip she stops reading (DL-3.3's `morningNote`, word for word).
export function deliveryNote(strip) {
  if (!strip || typeof strip !== "object") return "";
  if (strip.state === "quiet") return DELIVERY_QUIET_NOTE;
  if (strip.state === "broken") return DELIVERY_BROKEN_NOTE;
  return "";
}

// ---------------------------------------------------------------------------
// DT-4a — **ONE MEMBER'S CHAIN, WHICH IS THE FORM HER OWN VERIFICATION TAKES.**
//
// DT-4's own words: *"Her verification is NOT 'a message arrived' — it is the BEACON ROWS for her
// device showing received + displayed, the same evidence every member will produce."* `chainsForMember`
// has answered that question since DT-1 shipped and **nothing has ever asked it** — the same trap
// DT-1c found on the sweep half, one read over.
//
// **FOUR STATES, AND THE FOURTH IS THE ONE A THREE-STATE VERSION GETS WRONG.** A send that is inside
// the grace has not failed; it is a phone in a lift, and drawing it beside a real silence is how a
// diagnosis screen teaches somebody to ignore it (R18's distinction, and DL-3.3's argument about a
// strip that apologises on a good day).
export const CHAIN_STATES = [
  { key: "delivered",    label: "Delivered",        why: "The device reported it arrived and the notification drew." },
  { key: "drew-nothing", label: "Never drew",       why: "The worker reported it arrived and the notification never drew — the app on the device, not the endpoint." },
  { key: "silent",       label: "Never arrived",    why: "Sent, past the grace, and the device has said nothing at all." },
  { key: "waiting",      label: "Still in grace",   why: "Sent recently. A phone that is off or out of signal is not a defect yet." },
];
export const CHAIN_STATE_KEYS = CHAIN_STATES.map((s) => s.key);
export const chainState = (key) =>
  CHAIN_STATES.find((s) => s.key === String(key || "")) || null;

// `now` is a PARAMETER (R46/R142's precedent), so every state below is reachable in a fixture with no
// clock to wait on and no morning having had to go wrong first.
export function chainRow(row, { graceMinutes = 60, now = 0 } = {}) {
  const r = row && typeof row === "object" ? row : {};
  const sent = ms(r.sent_at ?? r.sentAt);
  const received = ms(r.received_at ?? r.receivedAt);
  const displayed = ms(r.displayed_at ?? r.displayedAt);
  // **THE ORDER IS THE LAW.** `displayed` is asked FIRST because it is the only stamp that means she
  // could have seen it; a row carrying it is delivered whatever else is missing, and reading
  // `received` first would file a re-delivery whose first stamp was lost as a failure.
  let state;
  if (displayed !== null) state = "delivered";
  else if (received !== null) state = "drew-nothing";
  else {
    // "I could not work out how old this is" is not "it is fresh" (R18/R19): with no send time, or no
    // clock to compare it to, the honest answer is the one that reports rather than the one that
    // quietly files it as still-in-grace and disappears.
    const minutes = sent === null || !now ? null : Math.round((now - sent) / 60_000);
    state = minutes === null || minutes >= Math.max(1, Number(graceMinutes) || 60) ? "silent" : "waiting";
  }
  return {
    tag: String(r.tag || ""), kind: String(r.kind || ""), device: String(r.device || ""),
    sentAt: r.sent_at ?? r.sentAt ?? null,
    minutes: sent === null || !now ? null : Math.round((now - sent) / 60_000),
    state,
  };
}

// The member-level answer. **THE ROWS ARE THE DELIVERABLE AND THE COUNTS RIDE BESIDE THEM**, because
// DT-4's verification is a chain she can read rather than a percentage — a member whose last twelve
// pushes all drew and whose thirteenth vanished is not "92% fine", she is one silent send.
export function memberDelivery(rows, { graceMinutes = 60, now = 0 } = {}) {
  const list = (Array.isArray(rows) ? rows : []).map((r) => chainRow(r, { graceMinutes, now }));
  const count = (k) => list.filter((r) => r.state === k).length;
  const counts = {
    delivered: count("delivered"), drewNothing: count("drew-nothing"),
    silent: count("silent"), waiting: count("waiting"),
  };
  // **NO SENDS IS NOT A HEALTHY PHONE** — DT-1c's `quiet` argument at member scale, and the state a
  // member who has never been pushed to is really in. Filing her as clean is the empty-set trap on
  // the one screen somebody opens to ask whether her phone works.
  const state = !list.length ? "never-sent"
    : (counts.silent || counts.drewNothing) ? "broken"
    : counts.delivered ? "clean" : "waiting";
  return { rows: list, counts, total: list.length, graceMinutes: Math.max(1, Number(graceMinutes) || 60), state };
}

export const MEMBER_NEVER_SENT_NOTE = "No push has ever been sent to this member, so nothing here can say whether her phone works.";
export const MEMBER_BROKEN_NOTE = "This member has sends the device never answered for. Until a beacon comes back those did not arrive.";

export function memberDeliveryNote(summary) {
  if (!summary || typeof summary !== "object") return "";
  if (summary.state === "never-sent") return MEMBER_NEVER_SENT_NOTE;
  if (summary.state === "broken") return MEMBER_BROKEN_NOTE;
  return "";
}
